import * as XLSX from "xlsx";
import { canonicalTeamName } from "./feeRules";

// -----------------------------
// Helpers
// -----------------------------
const pad2 = (n) => String(n).padStart(2, "0");

const toDateSafe = (d) => {
  if (!d) return null;
  if (d?.toDate) return d.toDate();
  if (d instanceof Date) return d;
  const dd = new Date(d);
  return Number.isNaN(dd.getTime()) ? null : dd;
};

const fmtDateTime = (d) => {
  const date = toDateSafe(d);
  if (!date) return "";
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}`;
};

const fmtDate = (d) => {
  const date = toDateSafe(d);
  if (!date) return "";
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const safeSheetName = (name) => {
  const invalidChars = ["\\", "/", "?", "*", "[", "]", ":"];
  const cleaned = invalidChars
    .reduce((value, char) => value.split(char).join(" "), String(name || "Sheet"))
    .trim();
  return cleaned.length > 31 ? cleaned.slice(0, 31) : cleaned || "Sheet";
};

const addAoASheet = (wb, sheetName, aoa) => {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));
};

const addJsonSheet = (wb, sheetName, rows) => {
  const ws = XLSX.utils.json_to_sheet(rows || []);
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));
};

const downloadWorkbook = (wb, filenameBase) => {
  const stamp = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(
      now.getHours()
    )}${pad2(now.getMinutes())}`;
  })();

  const filename = `${filenameBase}_${stamp}.xlsx`;
  XLSX.writeFile(wb, filename, { compression: true });
};

// -----------------------------
// DailyReport Export (เวอร์ชัน "ส่งบัญชี" - ชีตน้อย อ่านง่าย)
// ✅ ไม่มี: Daily per-day sheets / Zones / Day x Event
// ✅ มี: Summary, Records (All), Summary by Day, Summary by Event, Summary by Team
// -----------------------------
export function exportDailyReportToExcel({
  records = [],
  report = {},
  sumAll = {},
  filters = {},
  calcEffective,
  realNameByTeam = {},
  title = "รายงานรายรับ - ยยมือทองกดบัตร",
}) {
  const wb = XLSX.utils.book_new();

  // 1) Summary
  addAoASheet(wb, "Summary", [
    [title],
    ["สร้างไฟล์เมื่อ", fmtDateTime(new Date())],
    [],
    ["ตัวกรอง"],
    ["ชื่องาน", filters.selectedEvent || "all"],
    ["ทีม", filters.selectedTeam || "all"],
    ["วัน", filters.selectedDate || "all"],
    ["เดือน", filters.selectedMonth ?? "all"],
    ["ปี", filters.selectedYear ?? "all"],
    [
      "ช่วงวันที่",
      filters.dateFrom || filters.dateTo
        ? `${filters.dateFrom || ""} ถึง ${filters.dateTo || ""}`
        : "ไม่ได้เลือก",
    ],
    [],
    ["สรุปภาพรวม (ช่วงที่เลือก)"],
    ["ค่ากดรวม", Number(sumAll.totalFeeSum || 0)],
    ["หัก ณ ที่จ่ายรวม", Number(sumAll.totalTaxSum || 0)],
    ["รายได้ร้าน (หักทีม)", Number(sumAll.deductedFromTeam || 0)],
    ["รายได้จาก “ยย”", Number(sumAll.yyNet || 0)],
    ["โอนให้ทีม (ไม่รวมยย)", Number(sumAll.teamNetExcludingYY || 0)],
    ["รายได้ร้านรวมก่อนหักรายได้พิเศษ", Number(sumAll.storeIncome || 0)],
    ["รายได้พิเศษที่จ่ายทีม", Number(sumAll.extraIncomeNet || 0)],
    ["WHT ของรายได้พิเศษ", Number(sumAll.extraIncomeWht || 0)],
    ["ต้นทุนบริษัทรายได้พิเศษรวม", Number(sumAll.extraCompanyCost || 0)],
    ["บริษัทจ่ายเพิ่มให้ทีม (โอนเกินจากที่ทำได้)", Number(sumAll.salarySupport || 0)],
    ["กำไรหลังหักรายได้พิเศษ", Number(sumAll.profitAfterExtra ?? sumAll.storeIncome ?? 0)],
  ]);

  // 2) Records (All) + Summary by Day/Event/Team
  const byDay = new Map();
  const byEvent = new Map();
  const byTeam = new Map();

  const addAgg = (map, key, row) => {
    if (!map.has(key)) {
      map.set(key, {
        key,
        items: 0,
        qty: 0,
        baseFee: 0,
        deducted: 0,
        tax: 0,
        net: 0,
      });
    }
    const a = map.get(key);
    a.items += 1;
    a.qty += Number(row.จำนวนใบ || 0);
    a.baseFee += Number(row["ค่ากดรวม (base)"] || 0);
    a.deducted += Number(row["ถูกหักร้าน"] || 0);
    a.tax += Number(row["หัก ณ ที่จ่าย"] || 0);
    a.net += Number(row["ได้รับจริง"] || 0);
  };

  const parseDMY = (dmy) => {
    const [dd, mm, yy] = String(dmy || "").split("/").map(Number);
    if (!dd || !mm || !yy) return null;
    const dt = new Date(yy, mm - 1, dd);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const rawRows = records.map((r) => {
    const eff = calcEffective
      ? calcEffective(r)
      : { baseFee: 0, deducted: 0, tax: 0, net: 0, addFee: 0 };

    const dayStr = fmtDate(r.createdAt);
    const team = canonicalTeamName(r.teamName) || "ไม่ระบุทีม";
    const eventName = r.eventName || "ไม่ระบุงาน";

    const row = {
      เวลา: fmtDateTime(r.createdAt),
      วัน: dayStr,
      งาน: eventName,
      ทีม: team,
      "ชื่อจริง": realNameByTeam[team] || r.teamRealName || "",
      โซน: r.zone || r.priceZone || r.label || "",
      จำนวนใบ: Number(r.quantity || 0),
      "ค่ากด/ใบ": Number(r.feePerTicket || 0),
      "ค่ากดรวม (base)": Number(eff.baseFee || 0),
      "ถูกหักร้าน": Number(eff.deducted || 0),
      "หัก ณ ที่จ่าย": Number(eff.tax || 0),
      "ได้รับจริง": Number(eff.net || 0),
    };

    addAgg(byDay, dayStr, row);
    addAgg(byEvent, eventName, row);
    addAgg(byTeam, team, row);

    return row;
  });

  rawRows.sort((a, b) => {
    const da = parseDMY(a.วัน)?.getTime?.() || 0;
    const db = parseDMY(b.วัน)?.getTime?.() || 0;
    if (da !== db) return da - db;
    return String(a.เวลา).localeCompare(String(b.เวลา));
  });

  addJsonSheet(wb, "Records (All)", rawRows);

  const dayRows = Array.from(byDay.values())
    .map((a) => ({
      วันที่: a.key,
      รายการ: a.items,
      "จำนวนใบรวม": a.qty,
      "ค่ากดรวม (ก่อนหัก)": a.baseFee,
      "ถูกหักร้าน": a.deducted,
      "หัก ณ ที่จ่าย": a.tax,
      "ทีมได้รับจริง (สุทธิ)": a.net,
      "ร้านได้ (จากการหัก)": a.deducted,
    }))
    .sort((x, y) => (parseDMY(x.วันที่)?.getTime?.() || 0) - (parseDMY(y.วันที่)?.getTime?.() || 0));
  addJsonSheet(wb, "Summary by Day", dayRows);

  const eventRows = Array.from(byEvent.values())
    .map((a) => ({
      งาน: a.key,
      รายการ: a.items,
      "จำนวนใบรวม": a.qty,
      "ค่ากดรวม (ก่อนหัก)": a.baseFee,
      "ถูกหักร้าน": a.deducted,
      "หัก ณ ที่จ่าย": a.tax,
      "ทีมได้รับจริง (สุทธิ)": a.net,
      "ร้านได้ (จากการหัก)": a.deducted,
    }))
    .sort((a, b) => b["ค่ากดรวม (ก่อนหัก)"] - a["ค่ากดรวม (ก่อนหัก)"]);
  addJsonSheet(wb, "Summary by Event", eventRows);

  const teamRows = Array.from(byTeam.values())
    .map((a) => ({
      ทีม: a.key,
      "ชื่อจริง": realNameByTeam[a.key] || "",
      รายการ: a.items,
      "จำนวนใบรวม": a.qty,
      "ค่ากดรวม (ก่อนหัก)": a.baseFee,
      "ถูกหักร้าน": a.deducted,
      "หัก ณ ที่จ่าย": a.tax,
      "ทีมได้รับจริง (สุทธิ)": a.net,
      "ร้านได้ (จากการหัก)": a.deducted,
    }))
    .sort((a, b) => b["ทีมได้รับจริง (สุทธิ)"] - a["ทีมได้รับจริง (สุทธิ)"]);
  addJsonSheet(wb, "Summary by Team", teamRows);

  downloadWorkbook(wb, "DailyReport");
}
